// Practice time, firm side (PRD 64 §5).
//
// Sends `X-Firm-Id` like every other firm-scoped call — deliberately not routed
// through the business helpers, because a firm call that accidentally sent the
// business header would be a cross-tenant read.

import { apiGet, apiPost, apiPut } from '../utils/apiUtils';
import { parseData } from './businessApiCommon';
import { TimeEntrySummary, TimeWeek, TimeWeekRowInput } from '../types/practiceTimeApi';

const BASE = '/api/v1/firm/time';

const firmHeaders = (firmId: string) => ({ headers: { 'X-Firm-Id': firmId } });

export const practiceTimeService = {
  /** The grid, one payload. Any day in the week answers with the same week. */
  async getWeek(firmId: string, weekOf: string): Promise<TimeWeek> {
    const response = await apiGet(`${BASE}/week?week_of=${encodeURIComponent(weekOf)}`,
                                  firmHeaders(firmId));
    return parseData<TimeWeek>(response);
  },

  /**
   * The grid, ONE WRITE. Per-cell saves mean a half-saved week when somebody's
   * laptop closes — and a timesheet that is 60% written is worse than one that
   * is not written at all, because it looks finished.
   *
   * Answers with the week as it now stands rather than what was sent: a save
   * consolidates some cells and leaves others alone.
   */
  async saveWeek(firmId: string, weekOf: string, rows: TimeWeekRowInput[]): Promise<TimeWeek> {
    const response = await apiPut(`${BASE}/week`, { week_of: weekOf, rows }, firmHeaders(firmId));
    return parseData<TimeWeek>(response);
  },

  /** The running timer, if any — it lives on the server, not in this tab. */
  async runningTimer(firmId: string): Promise<TimeEntrySummary | null> {
    const response = await apiGet(`${BASE}/timer`, firmHeaders(firmId));
    return parseData<TimeEntrySummary | null>(response);
  },

  /** Starting a second stops the first and hands it back, so the UI can say so. */
  async startTimer(
    firmId: string,
    target: { work_item_id?: string; work_task_id?: string; firm_client_id?: string; note?: string },
  ): Promise<{ running: TimeEntrySummary; stopped: TimeEntrySummary | null }> {
    const response = await apiPost(`${BASE}/timer/start`, target, firmHeaders(firmId));
    return parseData<{ running: TimeEntrySummary; stopped: TimeEntrySummary | null }>(response);
  },

  async stopTimer(firmId: string): Promise<TimeEntrySummary> {
    const response = await apiPost(`${BASE}/timer/stop`, undefined, firmHeaders(firmId));
    return parseData<TimeEntrySummary>(response);
  },
};
